import {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { MentionAutocompletePanel } from "./MentionAutocompletePanel";
import type {
  MentionAutocompletePanelHandle,
  MentionUser,
} from "./MentionAutocompletePanel";
import { getMentionQuery, insertMention } from "@tinyclub/shared/utils";
import { cn } from "./cn";

// Re-export for convenience
export type { MentionUser, MentionAutocompletePanelHandle };

/** Autocomplete state for external rendering */
export interface MentionQueryState {
  query: string;
  startPosition: number;
}

/** Handle exposed by MentionTextarea for imperative actions */
export interface MentionTextareaHandle {
  /** Select a user and insert the mention at current cursor position */
  selectUser: (user: MentionUser) => void;
  /** Focus the textarea */
  focus: () => void;
  /** Get the textarea element for direct access (e.g., auto-resize) */
  getTextarea: () => HTMLTextAreaElement | null;
}

interface MentionTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  clubId: string;
  currentUserId: string;
  /** Position of the autocomplete panel relative to the textarea */
  panelPosition?: "above" | "below";
  /** Optional post ID to prioritize conversation participants in autocomplete results */
  contextPostId?: string;
  /** Additional class names for the container */
  containerClassName?: string;
}

export const MentionTextarea = forwardRef<
  MentionTextareaHandle,
  MentionTextareaProps
>(
  (
    {
      value,
      onChange,
      clubId,
      currentUserId,
      panelPosition = "below",
      contextPostId,
      containerClassName,
      className,
      onKeyDown,
      ...textareaProps
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const panelRef = useRef<MentionAutocompletePanelHandle>(null);
    const selectionInProgressRef = useRef(false);

    // Track cursor position in state to trigger re-evaluation when selection changes
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [mentionQuery, setMentionQuery] = useState<{
      query: string;
      startPosition: number;
    } | null>(null);

    // Refs to access current state values in callbacks
    const showAutocompleteRef = useRef(showAutocomplete);
    const mentionQueryRef = useRef(mentionQuery);
    useEffect(() => {
      showAutocompleteRef.current = showAutocomplete;
      mentionQueryRef.current = mentionQuery;
    }, [showAutocomplete, mentionQuery]);

    // Detect mention query whenever value or cursor changes
    useEffect(() => {
      // Skip detection if a selection is in progress
      if (selectionInProgressRef.current) return;

      const query = getMentionQuery(value, cursorPosition);
      if (query) {
        setMentionQuery(query);
        setShowAutocomplete(true);
      } else {
        setMentionQuery(null);
        setShowAutocomplete(false);
      }
    }, [value, cursorPosition]);

    // Handle text changes
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        const newCursor = e.target.selectionEnd;

        // Update cursor position
        setCursorPosition(newCursor);
        onChange(newText);
      },
      [onChange]
    );

    // Handle selection change (cursor movement)
    const handleSelect = useCallback(
      (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        const target = e.target as HTMLTextAreaElement;
        setCursorPosition(target.selectionEnd);
      },
      []
    );

    // Handle mention selection from autocomplete
    const handleMentionSelect = useCallback(
      (user: MentionUser) => {
        const currentMentionQuery = mentionQueryRef.current;
        if (!currentMentionQuery || !user.username) return;

        // Set flag to prevent useEffect from re-triggering panel during selection
        selectionInProgressRef.current = true;

        const { newText, newCursorPosition } = insertMention(
          value,
          user.username,
          currentMentionQuery.startPosition,
          cursorPosition
        );

        // Clear autocomplete state
        setShowAutocomplete(false);
        setMentionQuery(null);

        // Update cursor position and text
        setCursorPosition(newCursorPosition);
        onChange(newText);

        // Move cursor to end of inserted mention and clear the flag
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(
              newCursorPosition,
              newCursorPosition
            );
          }
          selectionInProgressRef.current = false;
        }, 0);
      },
      [value, cursorPosition, onChange]
    );

    // Handle closing the autocomplete panel
    const handleCloseAutocomplete = useCallback(() => {
      setShowAutocomplete(false);
      setMentionQuery(null);
    }, []);

    // Handle key press - navigate and select from autocomplete panel
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showAutocompleteRef.current) {
          // Handle Enter key - select highlighted result
          if (e.key === "Enter" && !e.shiftKey) {
            if (panelRef.current?.selectHighlighted?.() ?? panelRef.current?.selectFirst?.()) {
              e.preventDefault();
              return;
            }
          }

          // Handle arrow keys for navigation
          if (e.key === "ArrowDown") {
            if (panelRef.current?.moveDown?.()) {
              e.preventDefault();
              return;
            }
          }

          if (e.key === "ArrowUp") {
            if (panelRef.current?.moveUp?.()) {
              e.preventDefault();
              return;
            }
          }

          // Handle Escape to close panel
          if (e.key === "Escape") {
            handleCloseAutocomplete();
            e.preventDefault();
            return;
          }
        }

        // Call external handler if provided
        onKeyDown?.(e);
      },
      [onKeyDown, handleCloseAutocomplete]
    );

    // Handle blur - close panel after a short delay to allow panel tap to register
    const handleBlur = useCallback(() => {
      // Small delay to allow panel interactions to complete before closing
      setTimeout(() => {
        handleCloseAutocomplete();
      }, 150);
    }, [handleCloseAutocomplete]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        selectUser: handleMentionSelect,
        focus: () => textareaRef.current?.focus(),
        getTextarea: () => textareaRef.current,
      }),
      [handleMentionSelect]
    );

    return (
      <div className={cn("relative", containerClassName)}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={className}
          {...textareaProps}
        />
        {showAutocomplete && mentionQuery && (
          <>
            {/* Backdrop to catch clicks outside the panel */}
            <div
              className="fixed inset-0 z-40"
              onClick={handleCloseAutocomplete}
            />
            <MentionAutocompletePanel
              ref={panelRef}
              clubId={clubId}
              searchQuery={mentionQuery.query}
              currentUserId={currentUserId}
              onSelect={handleMentionSelect}
              onClose={handleCloseAutocomplete}
              visible={showAutocomplete}
              position={panelPosition}
              contextPostId={contextPostId}
            />
          </>
        )}
      </div>
    );
  }
);

MentionTextarea.displayName = "MentionTextarea";

export default MentionTextarea;
